import type { FC, HTMLAttributes } from 'react'
import { memo } from 'react'
import type { BaseArtworkItemPreviewCardProps } from 'src/components/Artwork/BaseArtworkItemPreviewCard'
import { useFit } from 'src/components/Common/FitToggle'
import { useTaskDisplay } from 'src/components/GenerationTask/Common/taskDisplay'
import {
  type Task,
  TaskCharacteristics,
  type TaskSummary,
} from 'src/services/apollo/generated/graphql'
import { isAnimateDiffTask as isAnimateDiffFullTask } from 'src/utils/task'
import BaseArtworkItemPreviewCard from '../Artwork/BaseArtworkItemPreviewCard'
import TaskPlaceholderImage from './TaskPlaceholderImage'

interface TaskPreviewCardProps extends HTMLAttributes<HTMLDivElement> {
  task?: Task
  taskSummary?: TaskSummary
  onTaskClick?: (task?: TaskSummary) => void
}

const TaskPreviewCard: FC<
  TaskPreviewCardProps & BaseArtworkItemPreviewCardProps
> = ({ taskSummary, task, onTaskClick, ...props }) => {
  const { id, promptsPreview, createdAt, characteristics } = taskSummary || {}
  const { effectiveStatus, mediaId } = useTaskDisplay({
    task: task ?? null,
    taskSummary: taskSummary ?? null,
  })
  const isPropsFullTask = !!task
  const isAnimated = Boolean(
    isPropsFullTask
      ? isAnimateDiffFullTask(task)
      : characteristics?.includes(TaskCharacteristics.AnimatedDiff),
  )
  const promptLabel =
    (isPropsFullTask ? task.parameters?.prompts : promptsPreview) ?? ''
  const fit = useFit('task-preview-card-fit')
  return (
    <BaseArtworkItemPreviewCard
      id={id}
      mediaId={mediaId}
      mediaFallback={
        <TaskPlaceholderImage
          className="w-full h-full"
          id={id}
          status={effectiveStatus}
        />
      }
      animated={isAnimated}
      label={promptLabel}
      time={createdAt}
      border="plain"
      fit={fit}
      {...props}
      onClick={(...args) => {
        props.onClick?.(...args)
        onTaskClick?.(taskSummary)
      }}
    />
  )
}

export default memo(TaskPreviewCard)
